// Minimal history router: the first slice has a handful of routes and no nested loaders.
import { type AnchorHTMLAttributes, type MouseEvent, type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Route =
  | { name: 'calls'; callId: string | null }
  | { name: 'contacts'; contactId: string | null; creating: boolean }
  | { name: 'messages'; threadId: string | null }
  | { name: 'voicemail' }
  | { name: 'team' }
  | { name: 'analytics' }
  | { name: 'live_call'; callId: string }
  | { name: 'admin' }
  | { name: 'routing' }
  | { name: 'flow' }
  | { name: 'blocklist' }
  | { name: 'settings'; section?: 'notifications' }
  | { name: 'phone' }
  | { name: 'call_link' }
  | { name: 'more' }
  | { name: 'not_found'; path: string };

export function parseRoute(pathname: string): Route {
  if (pathname === '/call') return { name: 'call_link' };
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean).map(decodeURIComponent);
  const [a, b, c] = parts;
  if (!a || (a === 'inbox' && (!b || b === 'calls'))) return { name: 'calls', callId: c ?? null };
  // The Google callback's error landing (identity/google.ts SIGN_IN_PATH): once signed in, it's Calls.
  if (a === 'sign-in' && !b) return { name: 'calls', callId: null };
  if (a === 'contacts') return b === 'new' ? { name: 'contacts', contactId: null, creating: true } : { name: 'contacts', contactId: b ?? null, creating: false };
  if (a === 'messages') return { name: 'messages', threadId: b ?? null };
  if (a === 'voicemail' && !b) return { name: 'voicemail' };
  if (a === 'team' && !b) return { name: 'team' };
  if (a === 'analytics') return { name: 'analytics' };
  if (a === 'live' && b === 'calls' && c && parts.length === 3) return { name: 'live_call', callId: c };
  if (a === 'admin' && b === 'routing' && !c) return { name: 'routing' };
  if (a === 'admin' && b === 'phone-menus' && !c) return { name: 'flow' };
  if (a === 'blocklist' && !b) return { name: 'blocklist' };
  if (a === 'admin') return { name: 'admin' };
  if (a === 'settings') return b === 'notifications' ? { name: 'settings', section: 'notifications' } : { name: 'settings' };
  if (a === 'phone') return { name: 'phone' };
  if (a === 'more') return { name: 'more' };
  return { name: 'not_found', path: pathname };
}

export function routeTitle(route: Route): string {
  switch (route.name) {
    case 'calls':
      return route.callId ? 'Call detail' : 'Calls';
    case 'contacts':
      return route.creating ? 'New contact' : route.contactId ? 'Contact' : 'Contacts';
    case 'messages':
      return route.threadId ? 'Conversation' : 'Messages';
    case 'voicemail':
      return 'Voicemail';
    case 'team':
      return 'Team';
    case 'analytics':
      return 'Analytics';
    case 'live_call':
      return 'Live call';
    case 'admin':
      return 'Admin';
    case 'routing':
      return 'Routing';
    case 'flow':
      return 'Phone menus';
    case 'blocklist':
      return 'Blocked and do not call';
    case 'settings':
      return route.section === 'notifications' ? 'Notifications' : 'Settings';
    case 'phone':
      return 'Phone';
    case 'call_link':
      return 'Review call';
    case 'more':
      return 'More';
    default:
      return 'Not found';
  }
}

interface RouterValue {
  route: Route;
  path: string;
  search: URLSearchParams;
  /** The raw query string ("" or "?…"), for routes that must check it exactly. */
  query: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

function splitPath(initialPath: string): { path: string; search: string } {
  const at = initialPath.indexOf('?');
  return at < 0 ? { path: initialPath, search: '' } : { path: initialPath.slice(0, at), search: initialPath.slice(at) };
}

export function RouterProvider({ children, initialPath }: { children: ReactNode; initialPath?: string }) {
  const [location, setLocation] = useState(() =>
    typeof window === 'undefined' ? splitPath(initialPath ?? '/') : { path: window.location.pathname, search: window.location.search },
  );
  useEffect(() => {
    const onPop = () => setLocation({ path: window.location.pathname, search: window.location.search });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    const url = new URL(to, window.location.origin);
    if (url.origin !== window.location.origin) return;
    if (options?.replace) window.history.replaceState(null, '', url.pathname + url.search);
    else window.history.pushState(null, '', url.pathname + url.search);
    setLocation({ path: url.pathname, search: url.search });
  }, []);
  const value = useMemo<RouterValue>(
    () => ({ route: parseRoute(location.path), path: location.path, search: new URLSearchParams(location.search), query: location.search, navigate }),
    [location, navigate],
  );
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

/** The router when one is mounted (in-app navigation), or null (server rendering, isolated components). */
export function useOptionalRouter(): RouterValue | null {
  return useContext(RouterContext);
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter outside RouterProvider');
  return value;
}

export function Link({ to, children, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; children: ReactNode }) {
  const { navigate } = useRouter();
  const handle = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} onClick={handle} {...rest}>
      {children}
    </a>
  );
}
